import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  canOperationalApprove,
  hasModuleAccess,
  hasRole,
  useAuth,
} from "../lib/auth";
import { Plus, Trash2 } from "lucide-react";
import FileUpload from "../components/FileUpload";
import PurchaseDetailsDialog from "../components/PurchaseDetailsDialog";
import AssetPurchaseEditDialog from "../components/AssetPurchaseEditDialog";
import { AssetPurchaseCancelDialog } from "../components/AssetPurchaseCancelDialog";

export default function AssetPurchases({ embedded = false }) {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const [settings, setSettings] = useState({});
  const canCreate =
    hasModuleAccess(user, "asset_management", "manage") &&
    hasModuleAccess(user, "purchase_register", "manage");
  const canApprovePurchases = canCreate && canOperationalApprove(user);
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newVendor, setNewVendor] = useState({ name: "", phone: "", gst_number: "" });
  const [po, setPo] = useState({
    vendor_id: "",
    vendor_name: "",
    invoice_no: "",
    po_number: "",
    bill_date: "",
    receiving_location: "A_STORE",
    room_id: "",
    department_id: "",
    attachment: null,
    remarks: "",
    lines: [{ item_id: "", qty: 1, rate: "", brand: "", warranty_end: "" }],
  });
  const [detailPurchase, setDetailPurchase] = useState(null);
  const [editPurchase, setEditPurchase] = useState(null);
  const [reversePurchase, setReversePurchase] = useState(null);

  const load = async () => {
    const [p, v, i, s, r, d] = await Promise.all([
      api.get("/assets/purchases"),
      api.get("/assets/vendors"),
      api.get("/assets/items", { params: { active: true } }),
      api.get("/assets/settings"),
      api.get("/rooms"),
      api.get("/assets/departments"),
    ]);
    setPurchases(p.data);
    setVendors(v.data);
    setItems(i.data);
    setSettings(s.data);
    setRooms(r.data);
    setDepartments(d.data);
  };
  useEffect(() => {
    load();
  }, []);

  const addVendor = async () => {
    if (!newVendor.name) return toast.error("Vendor name required");
    try {
      await api.post("/assets/vendors", newVendor);
      setNewVendor({ name: "", phone: "", gst_number: "" });
      toast.success("Vendor added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const canManageVendors = hasRole(user, "admin", "accounts", "hk_supervisor");
  const activeVendors = vendors.filter((v) => v.active !== false);

  const setLine = (idx, f, v) =>
    setPo((p) => ({
      ...p,
      lines: p.lines.map((l, i) => (i === idx ? { ...l, [f]: v } : l)),
    }));

  const createPo = async ({ directApprove = false } = {}) => {
    const clean = po.lines.filter((l) => l.item_id && Number(l.qty) > 0);
    if (!po.invoice_no || !po.bill_date || !clean.length) {
      return toast.error(
        "Bill number, bill date, and at least one line are required",
      );
    }
    if (po.receiving_location === "A_ROOM" && !po.room_id) {
      return toast.error("Choose the receiving room");
    }
    if (po.receiving_location === "A_DEPARTMENT" && !po.department_id) {
      return toast.error("Choose the receiving department");
    }
    const payload = {
      vendor_id: po.vendor_id || null,
      vendor_name: po.vendor_name || null,
      invoice_no: po.invoice_no,
      po_number: po.po_number || null,
      date: po.bill_date,
      bill_date: po.bill_date,
      receiving_location: po.receiving_location,
      room_id: po.receiving_location === "A_ROOM" ? po.room_id : null,
      department_id:
        po.receiving_location === "A_DEPARTMENT" ? po.department_id : null,
      attachment: po.attachment,
      remarks: po.remarks || null,
      lines: clean.map((line) => ({
        item_id: line.item_id,
        qty: Number(line.qty),
        rate: line.rate ? Number(line.rate) : null,
        brand: line.brand || null,
        warranty_end: line.warranty_end || null,
      })),
    };
    try {
      if (directApprove) {
        payload.operation_id =
          (crypto?.randomUUID && crypto.randomUUID()) ||
          `op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        await api.post("/assets/purchases/direct-approve", payload);
        toast.success("Purchase recorded & approved — stock updated");
      } else {
        await api.post("/assets/purchases", payload);
        toast.success("Purchase created (draft — needs approval)");
      }
      setPo({
        vendor_id: "",
        vendor_name: "",
        invoice_no: "",
        po_number: "",
        bill_date: "",
        receiving_location: "A_STORE",
        room_id: "",
        department_id: "",
        attachment: null,
        remarks: "",
        lines: [{ item_id: "", qty: 1, rate: "", brand: "", warranty_end: "" }],
      });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/assets/purchases/${id}/approve`);
      toast.success(
        `Approved — stock updated${
          data.units_created
            ? `, ${data.units_created} tagged unit(s) created`
            : ""
        }`,
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const itemName = (id) => items.find((i) => i.id === id)?.name || id;
  const totalBillValue = po.lines.reduce(
    (sum, line) => sum + Number(line.qty || 0) * Number(line.rate || 0),
    0,
  );

  const deletePurchase = async (purchase) => {
    if (!window.confirm(`Delete purchase ${purchase.number}?`)) {
      return;
    }
    try {
      await api.delete(`/assets/purchases/${purchase.id}`);
      toast.success("Purchase deleted");
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const content = (
    <>
      {canCreate ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <Card className="p-4">
            <div className="section-sub mb-3 font-medium text-slate-700">
              Vendors
            </div>
            {canManageVendors && (
              <div className="flex flex-wrap gap-2 mb-3">
                <Input
                  value={newVendor.name}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, name: e.target.value })
                  }
                  placeholder="Vendor name"
                  className="h-10 flex-1 min-w-[160px]"
                  data-testid="new-vendor-name"
                />
                <Input
                  value={newVendor.gst_number}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, gst_number: e.target.value })
                  }
                  placeholder="GST (optional)"
                  className="h-10 w-40"
                  data-testid="new-vendor-gst"
                />
                <Button
                  onClick={addVendor}
                  className="bg-slate-900 text-white"
                  data-testid="add-vendor"
                >
                  Add
                </Button>
              </div>
            )}
            {vendors.map((v) => (
              <div
                key={v.id}
                className="text-sm border-b border-slate-100 py-1.5"
              >
                <span className={v.active === false ? "text-slate-400 line-through" : ""}>
                  {v.name}
                </span>
                {v.gst_number && (
                  <span className="text-xs text-slate-500 ml-2">GST {v.gst_number}</span>
                )}
                {v.phone && (
                  <span className="text-xs text-slate-400 ml-2">{v.phone}</span>
                )}
              </div>
            ))}
          </Card>
          <Card className="p-4 lg:col-span-2">
            <div className="section-sub mb-3 font-medium text-slate-700">
              New Purchase / Receipt
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <div>
                <Label>Supplier</Label>
                <Select
                  value={po.vendor_id}
                  onValueChange={(v) =>
                    setPo({
                      ...po,
                      vendor_id: v,
                      vendor_name:
                        vendors.find((x) => x.id === v)?.name || po.vendor_name,
                    })
                  }
                >
                  <SelectTrigger className="h-10 mt-1" data-testid="po-vendor">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeVendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                        {v.gst_number ? ` — GST ${v.gst_number}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bill / Invoice Number</Label>
                <Input
                  value={po.invoice_no}
                  onChange={(e) => setPo({ ...po, invoice_no: e.target.value })}
                  className="h-10 mt-1"
                  data-testid="po-invoice"
                />
              </div>
              <div>
                <Label>Reference / PO Number</Label>
                <Input
                  value={po.po_number}
                  onChange={(e) => setPo({ ...po, po_number: e.target.value })}
                  className="h-10 mt-1"
                  data-testid="po-reference"
                />
              </div>
              <div>
                <Label>Bill Date</Label>
                <Input
                  type="date"
                  value={po.bill_date}
                  onChange={(e) => setPo({ ...po, bill_date: e.target.value })}
                  className="h-10 mt-1"
                  data-testid="po-bill-date"
                />
              </div>
              <div>
                <Label>Receive Into</Label>
                <Select
                  value={po.receiving_location}
                  onValueChange={(v) => setPo({ ...po, receiving_location: v })}
                >
                  <SelectTrigger
                    className="h-10 mt-1"
                    data-testid="po-receive-into"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A_STORE">Asset Store</SelectItem>
                    <SelectItem value="A_ROOM">Room</SelectItem>
                    <SelectItem value="A_DEPARTMENT">Department</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {po.receiving_location === "A_ROOM" && (
                <div>
                  <Label>Receiving Room</Label>
                  <Select
                    value={po.room_id}
                    onValueChange={(v) => setPo({ ...po, room_id: v })}
                  >
                    <SelectTrigger className="h-10 mt-1" data-testid="po-room">
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          Room {r.number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {po.receiving_location === "A_DEPARTMENT" && (
                <div>
                  <Label>Receiving Department</Label>
                  <Select
                    value={po.department_id}
                    onValueChange={(v) => setPo({ ...po, department_id: v })}
                  >
                    <SelectTrigger
                      className="h-10 mt-1"
                      data-testid="po-department"
                    >
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments
                        .filter((d) => d.active)
                        .map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Remarks</Label>
                <Input
                  value={po.remarks}
                  onChange={(e) => setPo({ ...po, remarks: e.target.value })}
                  className="h-10 mt-1"
                  data-testid="po-remarks"
                />
              </div>
            </div>
            {po.lines.map((l, idx) => (
              <div
                key={l.item_id || `row-${idx}`}
                className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2 items-end"
              >
                <div className="col-span-2">
                  <Label className="text-xs">Item</Label>
                  <Select
                    value={l.item_id}
                    onValueChange={(v) => setLine(idx, "item_id", v)}
                  >
                    <SelectTrigger
                      className="h-10 mt-1"
                      data-testid={`po-line-item-${idx}`}
                    >
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.name} ({i.tracking_mode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Qty</Label>
                  <Input
                    type="number"
                    min="1"
                    value={l.qty}
                    onChange={(e) => setLine(idx, "qty", e.target.value)}
                    className="h-10 mt-1"
                    data-testid={`po-line-qty-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Rate</Label>
                  <Input
                    type="number"
                    value={l.rate}
                    onChange={(e) => setLine(idx, "rate", e.target.value)}
                    className="h-10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Line Total</Label>
                  <div
                    className={
                      "h-10 mt-1 border rounded-md bg-slate-50 px-3 flex items-center text-sm"
                    }
                    data-testid={`po-line-total-${idx}`}
                  >
                    {(Number(l.qty || 0) * Number(l.rate || 0)).toFixed(2)}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Warranty End</Label>
                  <Input
                    type="date"
                    value={l.warranty_end}
                    onChange={(e) =>
                      setLine(idx, "warranty_end", e.target.value)
                    }
                    className="h-10 mt-1"
                  />
                </div>
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() =>
                      setPo((p) => ({
                        ...p,
                        lines: [
                          ...p.lines,
                          {
                            item_id: "",
                            qty: 1,
                            rate: "",
                            brand: "",
                            warranty_end: "",
                          },
                        ],
                      }))
                    }
                    data-testid={`po-add-line-${idx}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  {po.lines.length > 1 && (
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() =>
                        setPo((p) => ({
                          ...p,
                          lines: p.lines.filter((_, i) => i !== idx),
                        }))
                      }
                      data-testid={`po-remove-line-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <Label>Invoice Attachment</Label>
                <div className="mt-1">
                  <FileUpload
                    value={po.attachment}
                    onChange={(attachment) => setPo({ ...po, attachment })}
                    testid="po-attachment"
                  />
                </div>
              </div>
              <div
                className="self-end font-semibold text-sm"
                data-testid="po-total-bill-value"
              >
                Total Bill Value: {totalBillValue.toFixed(2)}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {canApprovePurchases ? (
                <>
                  <Button
                    onClick={() => createPo({ directApprove: true })}
                    className="bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="record-approve-po"
                  >
                    Record &amp; Approve Purchase
                  </Button>
                  <Button
                    onClick={() => createPo()}
                    variant="outline"
                    data-testid="create-po"
                  >
                    Save as Draft
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => createPo()}
                  className="bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="create-po"
                >
                  Create Purchase (Draft)
                </Button>
              )}
            </div>
          </Card>
        </div>
      ) : (
        hasRole(user, "hk_supervisor") && (
          <Card
            className="p-3 mb-4 text-sm text-slate-500"
            data-testid="po-disabled-hint"
          >
            Purchase drafts are Admin-only. Ask the Admin to enable "Allow
            Supervisor purchase drafts" in Asset Settings → Preferences.
          </Card>
        )
      )}

      <Card className="p-0 overflow-x-auto">
        <table className="vr-table" data-testid="asset-purchases-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Supplier</th>
              <th>Invoice</th>
              <th>PO</th>
              <th>Date</th>
              <th>Lines</th>
              <th>Total</th>
              <th>Status</th>
              <th>By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((p) => (
              <tr key={p.id}>
                <td className="font-mono text-xs">{p.number}</td>
                <td>{p.vendor_name || "—"}</td>
                <td className="text-xs">{p.invoice_no || "—"}</td>
                <td className="text-xs">{p.po_number || "—"}</td>
                <td className="text-xs">{p.bill_date || p.date}</td>
                <td className="text-xs">
                  {p.lines
                    .map((l) => `${itemName(l.item_id)}×${l.qty}`)
                    .join(", ")}
                </td>
                <td className="num">
                  {p.total_bill_value == null ? "—" : p.total_bill_value}
                </td>
                <td>
                  <Badge
                    className={
                      p.status === "approved"
                        ? "bg-emerald-100 border text-emerald-800"
                        : p.status === "cancelled"
                          ? "bg-slate-100 border text-slate-600"
                          : "bg-amber-100 border text-amber-800"
                    }
                  >
                    {p.status}
                  </Badge>
                </td>
                <td className="text-xs">{p.created_by_name}</td>
                <td>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDetailPurchase(p)}
                      data-testid={`asset-purchase-details-${p.id}`}
                    >
                      Details
                    </Button>
                    {canCreate && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditPurchase(p)}
                        data-testid={`asset-purchase-edit-${p.id}`}
                      >
                        Edit
                      </Button>
                    )}
                    {canApprovePurchases && ["draft", "cancelled"].includes(p.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => deletePurchase(p)}
                        data-testid={`asset-purchase-delete-${p.id}`}
                      >
                        Delete
                      </Button>
                    )}
                    {canApprovePurchases && p.status === "draft" && (
                      <>
                        <Button
                          size="sm"
                          className="bg-slate-900 text-white"
                          onClick={() => approve(p.id)}
                          data-testid={`approve-po-${p.number}`}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                    {canApprovePurchases && ["draft", "approved"].includes(p.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReversePurchase(p)}
                        data-testid={`reverse-po-${p.number}`}
                      >
                        Reverse
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );

  const withDialogs = (
    <>
      {content}
      <PurchaseDetailsDialog
        open={!!detailPurchase}
        onOpenChange={(open) => !open && setDetailPurchase(null)}
        purchase={detailPurchase}
        endpoint="/assets/purchases"
        testid="asset-purchase"
      />
      <AssetPurchaseEditDialog
        open={!!editPurchase}
        onOpenChange={(open) => !open && setEditPurchase(null)}
        purchase={editPurchase}
        vendors={vendors}
        items={items}
        rooms={rooms}
        departments={departments}
        onSaved={() => load()}
      />
      <AssetPurchaseCancelDialog
        open={!!reversePurchase}
        onOpenChange={(open) => !open && setReversePurchase(null)}
        purchase={reversePurchase}
        onSaved={() => load()}
      />
    </>
  );

  if (embedded) return withDialogs;
  return (
    <Layout>
      <div className="mb-4">
        <h1 className="section-title">Purchases &amp; Vendors</h1>
      </div>
      <AssetNav />
      {withDialogs}
    </Layout>
  );
}
